import firebase from "firebase/compat";
import { Box, Button, TextField } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";

import CustomCard from "../../components/CustomCard";
import Loader from "../../components/Loader";
import Spacing from "../../components/Spacing";
import useCurrentUser from "../../hooks/useCurrentUser";
import { gymDocRef } from "../../utils/referencies";
import { applyUpdateTimestamp } from "../../utils/timestamp";
import { useNotify, useRedirect } from "react-admin";
import useDisabledOnLoading from "../../hooks/useDisabledOnLoading";
import ProfileLogo from "./components/ProfileLogo";
import { uploadedLogo, uploadedImages } from "./utils/uploadedImages";
import { QATAR_CODE_PREFIX } from "../../constants/general";
import Location from "../../components/Location";
import { geohashForLocation } from "geofire-common";
import ProfileImages from "./components/ProfileImages";
import SocialMediaLinks from "../Gym/components/SocialMediaLinks";
import useGetEnumsAsChoices from "../../hooks/useGetEnumsAsChoices";
import { Enums } from "../../types/resource.types";
import CustomTextField from "./components/CustomTextField";
import CustomSelectField from "./components/CustomSelectField";
import { isEmpty } from "ramda";
import { GenericObject } from "../../types/generic.types";
import { areEqual } from "../../utils/generic";
import firebaseConfig from "../../init/config";
import OpeningHours from "../../components/OpeningHours";
import { deleteMaterialDocuments, getVideosDifference, MaterialDocument } from "./utils/videosUploadint.utils";
import useFirebaseFileUploader from "./components/useFirebaseFileUploader";
import VideosUpload from "./components/VideosUpload";

const GymProfile = () => {
  const { user } = useCurrentUser();
  const notify = useNotify();
  const typesStatus = useGetEnumsAsChoices<Enums>(Enums.gymType);
  const redirect = useRedirect();

  const {
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
    control,
  } = useForm<any>({ mode: "onChange" });

  const [loadingFetching, setLoadingFetching] = useState(false);
  const [fetchedData, setFetchedData] = useState<any>(null);
  const [profileLogo, setProfileLogo] = useState<any>(null);
  const [profileImages, setProfileImages] = useState<any>([]);
  const [socials, setSocials] = useState<any>(null);

  const [socialLinksError, setSocialLinksError] = useState(false);
  const [timingError, setTimingError] = useState(true);
  const [timing, setTimings] = useState<any>(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [enteredLocationUrl, setEnteredLocationUrl] = useState<GenericObject | null>(null);
  const [location, setLocation] = useState<GenericObject | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);

  const [originalVideos, setOriginalVideos] = useState<MaterialDocument[]>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const { uploadProgress, handleUpload, uploading } = useFirebaseFileUploader(
    gymDocRef(user.id!).collection("materials")
  );
  const { disabledStyles } = useDisabledOnLoading(loadingSubmit || locationLoading || uploading);

  const onSubmit: SubmitHandler<any> = async (data) => {
    setLoadingSubmit(true);
    let addedVideos: any[] = [];

    let finalData = data;
    if (!data.password) {
      const { password, ...restData } = data;
      finalData = restData;
    }

    try {
      if (videos.length) {
        /**
         * as backend implementation, we should create empty document for each video in the user materials sub collection
         * as after uploading video to firestore storage, backend will update in these created documents
         * */
        const result = getVideosDifference(videos, originalVideos);
        addedVideos = result.difference;
        console.log({ addedVideos });

        if (addedVideos.length) {
          await handleUpload(addedVideos);
        }
      }
      const selectedUploadedLogo = profileLogo ? await uploadedLogo(profileLogo) : fetchedData?.logo;
      const selectedUploadedImages = profileImages.length ? await uploadedImages(profileImages) : fetchedData?.images;

      await gymDocRef(user.id!).update(
        applyUpdateTimestamp({
          ...finalData,
          phone: `+974${data.phone}`,
          whatsapp: `+974${data.whatsapp}`,
          logo: selectedUploadedLogo,
          images: selectedUploadedImages,
          socials,
          openingHours: timing,
          location,
          type: data.type ?? fetchedData.type,
        })
      );
      const { difference: removedVideos } = getVideosDifference(originalVideos, videos);
      console.log({ removedVideos });
      if (removedVideos.length) {
        // delete removed videos after all updates
        await deleteMaterialDocuments(removedVideos as MaterialDocument[]);
        const { difference: restVideos } = getVideosDifference(originalVideos, removedVideos);
        console.log({ restVideos });
        setOriginalVideos([...restVideos, ...addedVideos]);
      }
      notify("Porfile has been updated successfully", "success");
      redirect("/");

      setLoadingSubmit(false);
    } catch (ex: any) {
      console.log(ex.message);
      notify("Error on updating profile", "error");
      setLoadingSubmit(false);

      // remove added videos if there is any error
      if (addedVideos.length) await deleteMaterialDocuments(addedVideos);
    }
  };

  const fetchData = async () => {
    setLoadingFetching(true);
    try {
      const snapshot = await gymDocRef(user.id!).get();
      if (snapshot.exists) {
        const data = snapshot?.data();
        setSocials(data?.socials);
        setTimings(data?.openingHours);
        setFetchedData({
          ...data,
          phone: data?.phone.replace(QATAR_CODE_PREFIX, ""),
          whatsapp: data?.whatsapp.replace(QATAR_CODE_PREFIX, ""),
        });
        setLoadingFetching(false);
      }
    } catch (ex: any) {
      console.log(ex.message);
      setLoadingFetching(false);
    }
  };

  const handleLocationUrl = async (e: any) => {
    if (e.target.value.trim()) {
      try {
        setLocationLoading(true);
        const response = await fetch(
          `${firebaseConfig.cloudFunctionsBaseUrl}/functions-getLatLng?url=${e.target.value}`
        );
        const data = await response.json();
        setEnteredLocationUrl(data);
        setLocationLoading(false);
      } catch (ex: any) {
        console.log(ex.message);
        setLocationLoading(false);
      }
    }
  };

  const handleSelectVideos = (selectedVideos: any[]) => {
    setVideos(selectedVideos);
    const errorFiltered = selectedVideos.filter((video: any) => !video.type.startsWith("video"));
    if (errorFiltered.length)
      setError("videos", {
        type: "required",
        message: "Upload videos only",
      });
    else {
      clearErrors("videos");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // useEffect(() => {
  //   if (videos.length === 0) {
  //     setError("videos", {
  //       type: "required",
  //       message: "Videos are required",
  //     });
  //   } else {
  //     clearErrors("videos");
  //   }
  // }, [videos, setError, clearErrors]);

  useEffect(() => {
    (async function () {
      const materialSnapshots = await gymDocRef(user.id!)
        .collection("materials")
        .where("status", "==", "uploaded")
        .get();
      if (materialSnapshots.size) {
        setOriginalVideos(
          materialSnapshots.docs.map((snap) => ({ ...snap.data(), ref: snap.ref })) as MaterialDocument[]
        );
        setVideos(materialSnapshots.docs.map((snap) => ({ ...snap.data(), ref: snap.ref })) as MaterialDocument[]);
      }
    })();
  }, [user.id]);

  return (
    <Loader loading={loadingFetching}>
      <Box style={disabledStyles}>
        {fetchedData ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <CustomCard width="50%" title="Details">
              <Spacing>
                <CustomTextField
                  disabled={true}
                  control={control}
                  name="displayName.en"
                  label="Display Name (en)"
                  defaultValue={fetchedData.displayName.en}
                  rules={{
                    required: "Required",
                    maxLength: {
                      value: 50,
                      message: "Must be 32 characters or less",
                    },
                    minLength: {
                      value: 2,
                      message: "Must be 2 characters at least",
                    },
                  }}
                />
                <CustomTextField
                  disabled={true}
                  control={control}
                  name="displayName.ar"
                  label="Display Name (ar)"
                  defaultValue={fetchedData.displayName.ar}
                  rules={{
                    required: "Required",
                    maxLength: {
                      value: 50,
                      message: "Must be 32 characters or less",
                    },
                    minLength: {
                      value: 2,
                      message: "Must be 2 characters at least",
                    },
                  }}
                />
              </Spacing>

              <Spacing>
                <CustomTextField
                  disabled={true}
                  control={control}
                  name="email"
                  label="Email"
                  defaultValue={fetchedData.email}
                  rules={{
                    required: "Required",
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: "Invalid Email",
                    },
                  }}
                />
                <CustomTextField
                  control={control}
                  name="emailingMail"
                  label="Emailing mail"
                  defaultValue={fetchedData.emailingMail}
                  rules={{
                    required: "Required",
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: "Invalid Email",
                    },
                  }}
                />
              </Spacing>
              <CustomTextField
                control={control}
                name="password"
                label="Password"
                defaultValue={fetchedData.password}
                type="password"
                rules={{
                  minLength: {
                    value: 8,
                    message: "Must be 8 characters at least",
                  },
                }}
              />

              <Spacing>
                <CustomTextField
                  control={control}
                  name="phone"
                  label="Phone number"
                  type="number"
                  defaultValue={fetchedData.phone}
                  rules={{
                    required: "Required",
                    minLength: {
                      value: 8,
                      message: "Invalid Number",
                    },
                    maxLength: {
                      value: 8,
                      message: "Invalid Number",
                    },
                  }}
                  startAdornment={true}
                />
                <CustomTextField
                  control={control}
                  name="whatsapp"
                  label="Whatsapp number"
                  type="number"
                  defaultValue={fetchedData.whatsapp}
                  rules={{
                    required: "Required",
                    minLength: {
                      value: 8,
                      message: "Invalid Number",
                    },
                    maxLength: {
                      value: 8,
                      message: "Invalid Number",
                    },
                  }}
                  startAdornment={true}
                />
              </Spacing>

              <Spacing>
                <CustomTextField
                  control={control}
                  name="description.en"
                  label="Description (en)"
                  minRows={5}
                  maxRows={5}
                  multiline={true}
                  defaultValue={fetchedData.description.en}
                  rules={{
                    required: "Required",
                    maxLength: {
                      value: 256,
                      message: "Must be 256 characters or less",
                    },
                    minLength: {
                      value: 20,
                      message: "Must be 20 characters at least",
                    },
                  }}
                />
                <CustomTextField
                  control={control}
                  name="description.ar"
                  label="Description (ar)"
                  minRows={5}
                  maxRows={5}
                  multiline={true}
                  defaultValue={fetchedData.description.ar}
                  rules={{
                    required: "Required",
                    maxLength: {
                      value: 256,
                      message: "Must be 256 characters or less",
                    },
                    minLength: {
                      value: 20,
                      message: "Must be 20 characters at least",
                    },
                  }}
                />
              </Spacing>

              <CustomSelectField
                control={control}
                name="type"
                label="Type"
                defaultValue={fetchedData.type}
                listItems={typesStatus}
              />
            </CustomCard>

            <CustomCard title="Social media links" width="50%">
              <SocialMediaLinks
                handleSocialLinks={(socialLinks: any) => setSocials(socialLinks)}
                handleSocialLinksError={(value: boolean) => setSocialLinksError(value)}
                defaultValue={fetchedData.socials}
              />
            </CustomCard>

            <OpeningHours
              onChangeTiming={(timing: any) => {
                setTimings(timing);
              }}
              setError={setTimingError}
              timingData={fetchedData.openingHours}
              width="50%"
            />

            <ProfileLogo logo={fetchedData.logo} setUploadLogo={(uploadedLogo: any) => setProfileLogo(uploadedLogo)} />
            <ProfileImages
              images={fetchedData.images}
              onSelectImages={(selectedImages: any) => {
                setProfileImages(selectedImages);
              }}
            />

            <CustomCard title="Videos" width="50%">
              <VideosUpload
                originalVideos={originalVideos}
                onSelect={handleSelectVideos}
                progress={uploadProgress.progress}
              />
            </CustomCard>

            <CustomCard width="50%" title="Address">
              <Spacing>
                <CustomTextField
                  control={control}
                  name="address.details.en"
                  label="Address (en)"
                  minRows={5}
                  maxRows={5}
                  multiline={true}
                  defaultValue={fetchedData.address.details.en}
                  rules={{
                    required: "Required",
                    maxLength: {
                      value: 256,
                      message: "Must be 256 characters or less",
                    },
                    minLength: {
                      value: 20,
                      message: "Must be 20 characters at least",
                    },
                  }}
                />
                <CustomTextField
                  control={control}
                  name="address.details.ar"
                  label="Address (ar)"
                  minRows={5}
                  maxRows={5}
                  multiline={true}
                  defaultValue={fetchedData.address.details.ar}
                  rules={{
                    required: "Required",
                    maxLength: {
                      value: 256,
                      message: "Must be 256 characters or less",
                    },
                    minLength: {
                      value: 20,
                      message: "Must be 20 characters at least",
                    },
                  }}
                />
              </Spacing>
            </CustomCard>
            <CustomCard width="50%" title="Location">
              <Box mr={2} mb={1}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  onChange={handleLocationUrl}
                  label="Location url"
                />
              </Box>
              <Location
                onLocationChange={(data) => {
                  if (!areEqual(data, enteredLocationUrl)) {
                    setEnteredLocationUrl(data);
                    const geohash = geohashForLocation([data.lat, data.lng]);
                    const geopoint = new firebase.firestore.GeoPoint(data.lat, data.lng);
                    setLocation({ geohash, geopoint });
                  }
                }}
                value={
                  enteredLocationUrl && (!isNaN(+enteredLocationUrl.lat) || !isNaN(+enteredLocationUrl.lng))
                    ? { latitude: +enteredLocationUrl.lat, longitude: +enteredLocationUrl.lng }
                    : null
                }
                defaultValue={fetchedData.location.geopoint}
                // onLocationChange={(data) => {
                //   if (data?.lat && data?.lng) {
                //     const geohash = geohashForLocation([data.lat, data.lng]);
                //     const geopoint = new firebase.firestore.GeoPoint(data.lat, data.lng);
                //     setLocation({ geohash, geopoint });
                //   }
                // }}
                // defaultValue={fetchedData.location.geopoint}
              />
            </CustomCard>

            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isEmpty(errors) || socialLinksError || timingError}
            >
              Save
            </Button>
          </form>
        ) : null}
      </Box>
    </Loader>
  );
};

export default GymProfile;
